import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Progress,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import MercadoPagoCardBrick, {
  hasMercadoPagoPublicKey,
} from "../../../../Components/Billing/MercadoPagoCardBrick";
import PayPalSubscribeButton from "../../../../Components/Billing/PayPalSubscribeButton";
import {
  helpArticleUrl,
  helpUrlForRoute,
} from "../../../../constants/helpRouteMap";
import {
  formatCents,
  formatCentsPerThousand,
  intlLocaleFromI18n,
} from "../../../../helpers/money";
import {
  ActiveCoupon,
  cancelSubscription,
  checkoutSubscription,
  CouponPreview,
  discountPercentFromBps,
  downloadInvoiceReceipt,
  openInvoiceReceipt,
  resendInvoiceReceipt,
  effectiveQuota,
  FAKE_CARD_TOKEN_ID,
  getPublicBillingGateways,
  getPublicPlans,
  getSubscription,
  getSubscriptionInvoices,
  preparePayPalCheckout,
  PublicBillingGateways,
  purchaseQuotaPack,
  QUOTA_PACK_EMAILS,
  QUOTA_PACK_MAX_PER_PERIOD,
  QUOTA_PACK_PRICE_CENTS,
  QUOTA_PACK_USD_PRICE_CENTS,
  resumeSubscription,
  SaasBillingInvoice,
  Subscription,
  SubscriptionPlan,
  updatePaymentMethod,
  validateCheckoutCoupon,
} from "../../../../helpers/subscription_helper";

/** Intervalo de reconsulta enquanto o pagamento está pendente (sem ETA do gateway). */
const PENDING_POLL_SECONDS = 20;

/** Copy leigo do status da promo ativa (LAC-089) — sem jargão de gateway. */
const activeCouponStatusMessage = (
  coupon: ActiveCoupon,
  t: (key: string, opts?: Record<string, unknown>) => string,
  amountLabel: string | null
): string => {
  const percent = discountPercentFromBps(coupon.discount_percent_bps);
  if (coupon.duration_type === "lifetime") {
    return t("settings.subscription.activeCoupon.lifetime", {
      code: coupon.code,
      percent,
      amount: amountLabel || "—",
    });
  }
  const remaining =
    coupon.cycles_remaining != null && coupon.cycles_remaining > 0
      ? coupon.cycles_remaining
      : 1;
  return t("settings.subscription.activeCoupon.remainingInvoices", {
    code: coupon.code,
    percent,
    count: remaining,
    amount: amountLabel || "—",
  });
};

/**
 * Assinatura do tenant (LAC-018 + LAC-026 UI): plano, Brick MP, cancel, pack +10k, faturas.
 */
const SubscriptionSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  document.title = t("settings.subscription.documentTitle");

  // Signup sem Trial: vem do Register com requiresCheckout + assinatura pending.
  const fromRegisterCheckout = !!(
    location.state as { requiresCheckout?: boolean } | null
  )?.requiresCheckout;

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [invoices, setInvoices] = useState<SaasBillingInvoice[]>([]);
  const [gateways, setGateways] = useState<PublicBillingGateways | null>(null);
  const [catalogCurrency, setCatalogCurrency] = useState<"BRL" | "USD">("BRL");
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<SubscriptionPlan | null>(
    null
  );
  const [checkingOut, setCheckingOut] = useState(false);
  const [downgradeAck, setDowngradeAck] = useState(false);
  const [pendingCountdown, setPendingCountdown] = useState(PENDING_POLL_SECONDS);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelWhen, setCancelWhen] = useState<"now" | "period_end">(
    "period_end"
  );
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [packOpen, setPackOpen] = useState(false);
  const [oneClickOpen, setOneClickOpen] = useState(false);
  const [changeCardOpen, setChangeCardOpen] = useState(false);
  const [changingCard, setChangingCard] = useState(false);
  const [buyingPack, setBuyingPack] = useState(false);
  const [couponEnabled, setCouponEnabled] = useState(false);
  const [couponDraft, setCouponDraft] = useState("");
  const [couponPreview, setCouponPreview] = useState<CouponPreview | null>(
    null
  );
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  const locale = intlLocaleFromI18n(i18n.language);
  const useBrick = hasMercadoPagoPublicKey();
  const usdReady = !!gateways?.usd?.ready;
  const paypalClientId = gateways?.usd?.paypal_client_id || null;
  const helpHref = helpUrlForRoute(
    "/settings/subscription",
    i18n.language || "pt-BR"
  );
  // Artigo específico de cupom (não o de cota) — Carla no checkout.
  const couponHelpHref = helpArticleUrl(
    "/billing/coupons",
    i18n.language || "pt-BR"
  );
  const paypalHelpHref = helpArticleUrl(
    "/billing/paypal",
    i18n.language || "pt-BR"
  );

  const formatDate = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    try {
      return new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(iso));
    } catch {
      return null;
    }
  };

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = !!opts?.silent;
      if (!silent) {
        setLoading(true);
        setUnavailable(false);
      }
      try {
        const [subRes, gatewaysRes, invoicesRes] = await Promise.all([
          getSubscription(),
          getPublicBillingGateways(),
          getSubscriptionInvoices({ per_page: 25 }),
        ]);

        if (subRes?.success && subRes.data?.plan) {
          setSubscription(subRes.data);
          setUnavailable(false);
        } else if (!silent) {
          setSubscription(null);
          setUnavailable(true);
        }

        // Gateways USD/BRL — default_currency = preferência da plataforma.
        if (gatewaysRes?.success && gatewaysRes.data) {
          setGateways(gatewaysRes.data);
          // Default catalog: subscription currency > tenant preference > platform default > BRL.
          const defaultCurr =
            subRes.data?.currency ||
            gatewaysRes.data.default_currency ||
            "BRL";
          setCatalogCurrency(defaultCurr === "USD" ? "USD" : "BRL");
        } else {
          setGateways(null);
          setCatalogCurrency("BRL");
        }

        // Carregar planos de AMBAS moedas se USD ready (ou sempre carregar ambos).
        const usdReady = gatewaysRes?.data?.usd?.ready ?? false;
        const currencies = usdReady ? ["BRL", "USD"] : ["BRL"];
        const plansPromises = currencies.map((curr) => getPublicPlans(curr));
        const plansResults = await Promise.all(plansPromises);
        const allPlans = plansResults.flatMap((res) =>
          res?.success ? res.data || [] : []
        );
        setPlans(allPlans);

        setInvoices(invoicesRes?.success ? invoicesRes.data || [] : []);
      } catch (e: any) {
        if (!silent) {
          setSubscription(null);
          setGateways(null);
          setUnavailable(true);
          setPlans([]);
          setInvoices([]);
          toast.error(e?.message || t("settings.subscription.loadError"), {
            autoClose: 4000,
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const isPending = subscription?.status === "pending";
  const externallyBilled = subscription?.billing_channel === "brdominio";
  // Signup pago (Trial ausente do catálogo): pending sem fatura = escolher/pagar plano.
  const awaitingFirstCheckout =
    isPending &&
    !!subscription?.plan &&
    !subscription.plan.is_trial_plan &&
    (fromRegisterCheckout || invoices.length === 0);
  const checkoutLocked = isPending && !awaitingFirstCheckout;
  const canManageBilling =
    !externallyBilled &&
    (subscription?.status === "active" ||
      subscription?.status === "past_due" ||
      awaitingFirstCheckout);

  // Pack MP pode ficar open até o webhook — mesmo poll do pending de assinatura.
  const hasOpenPackInvoice = invoices.some(
    (inv) => inv.kind === "quota_pack" && inv.status === "open"
  );
  // Não pollar no “ainda não pagou” do signup — só após iniciar checkout/gateway.
  const needsPaymentPoll =
    (isPending && !awaitingFirstCheckout) || hasOpenPackInvoice;

  // Enquanto pagamento em aberto: countdown + refresh silencioso.
  useEffect(() => {
    if (!needsPaymentPoll) {
      setPendingCountdown(PENDING_POLL_SECONDS);
      return;
    }

    setPendingCountdown(PENDING_POLL_SECONDS);
    const tick = window.setInterval(() => {
      setPendingCountdown((prev) => {
        if (prev <= 1) {
          void load({ silent: true });
          return PENDING_POLL_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(tick);
  }, [needsPaymentPoll, load]);

  useEffect(() => {
    if (!checkoutPlan) setDowngradeAck(false);
  }, [checkoutPlan]);

  const resetCouponState = () => {
    setCouponEnabled(false);
    setCouponDraft("");
    setCouponPreview(null);
    setCouponError(null);
    setValidatingCoupon(false);
  };

  const checkoutAmountCents = useMemo(() => {
    if (!checkoutPlan) return 0;
    if (couponPreview) return couponPreview.total_cents;
    return checkoutPlan.base_price_cents || 0;
  }, [checkoutPlan, couponPreview]);

  // Deep-link do dashboard: ?buyPack=1 abre o modal do pack (se elegível).
  useEffect(() => {
    if (searchParams.get("buyPack") !== "1" || loading || !subscription) {
      return;
    }
    const packsNow = subscription.quota_packs_in_period ?? 0;
    const quotaNow = effectiveQuota(subscription);
    const usedNow = subscription.emails_used_in_period ?? 0;
    const pct =
      quotaNow > 0 ? Math.min(100, Math.round((usedNow / quotaNow) * 100)) : 0;
    const eligible =
      subscription.billing_channel !== "brdominio" &&
      (subscription.status === "active" ||
        subscription.status === "past_due") &&
      packsNow < QUOTA_PACK_MAX_PER_PERIOD &&
      pct >= 80;
    if (eligible) {
      setPackOpen(true);
    }
    searchParams.delete("buyPack");
    setSearchParams(searchParams, { replace: true });
  }, [loading, subscription, searchParams, setSearchParams]);

  // Deep-link dos e-mails de cobrança: ?focus=payment destaca o bloco de status/pagamento.
  const [paymentFocus, setPaymentFocus] = useState(false);
  useEffect(() => {
    if (searchParams.get("focus") !== "payment" || loading) {
      return;
    }
    setPaymentFocus(true);
    const el = document.getElementById("subscription-payment-focus");
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    searchParams.delete("focus");
    setSearchParams(searchParams, { replace: true });
    const timer = window.setTimeout(() => setPaymentFocus(false), 8000);
    return () => window.clearTimeout(timer);
  }, [loading, searchParams, setSearchParams]);

  const used = subscription?.emails_used_in_period ?? 0;
  // Threshold 80% e barra usam cota efetiva (plano + packs), não só o plano.
  const quota = effectiveQuota(subscription);
  const packs = subscription?.quota_packs_in_period ?? 0;
  const remaining = Math.max(quota - used, 0);
  const percent =
    quota > 0 ? Math.min(100, Math.round((used / quota) * 100)) : 0;
  const nearLimit = quota > 0 && percent >= 80;
  const exhausted = quota > 0 && used >= quota;
  const canBuyPack =
    canManageBilling &&
    !isPending &&
    !hasOpenPackInvoice &&
    packs < QUOTA_PACK_MAX_PER_PERIOD &&
    nearLimit;
  const packsMaxReached =
    nearLimit && packs >= QUOTA_PACK_MAX_PER_PERIOD;
  const packPreviewQuota = quota + QUOTA_PACK_EMAILS;
  const progressColor = exhausted
    ? "danger"
    : nearLimit
      ? "warning"
      : "success";

  const periodStart = formatDate(subscription?.current_period_start);
  const periodEnd = formatDate(subscription?.current_period_end);
  const plan = subscription?.plan;
  const currency = plan?.currency || subscription?.currency || "BRL";
  const intervalKey = plan?.billing_interval
    ? `settings.subscription.interval.${plan.billing_interval}`
    : null;

  const upgradePlans = useMemo(() => {
    if (!plan) return [];
    // Catálogo público = só mensal (anual proibido — DOC 10).
    // Signup pago: inclui o plano já atribuído (pending) para o 1º checkout.
    return plans.filter((p) => {
      if (p.is_trial_plan || p.is_active === false) {
        return false;
      }
      if (p.id === plan.id && !awaitingFirstCheckout) {
        return false;
      }
      if (p.currency !== catalogCurrency) {
        return false;
      }
      const interval = (p.billing_interval || "monthly").toLowerCase();
      return interval === "monthly" || interval === "month";
    });
  }, [plans, plan, catalogCurrency, awaitingFirstCheckout]);

  const isDowngrade = useMemo(() => {
    if (!checkoutPlan || !plan) return false;
    return (checkoutPlan.email_quota || 0) < (plan.email_quota || 0);
  }, [checkoutPlan, plan]);

  const canConfirmCheckout = !isDowngrade || downgradeAck;
  const busy = checkingOut || cancelling || buyingPack || resuming || changingCard;

  const statusBadgeColor = (status: string): string => {
    if (status === "active" || status === "paid") return "success";
    if (
      status === "pending" ||
      status === "open" ||
      status === "past_due" ||
      status === "pending_cancel"
    )
      return "warning";
    if (status === "failed" || status === "canceled" || status === "suspended")
      return "danger";
    return "secondary";
  };

  const openCheckout = (p: SubscriptionPlan) => {
    if (checkoutLocked || busy) return;
    setDowngradeAck(false);
    resetCouponState();
    setCheckoutPlan(p);
  };

  const applyCoupon = async () => {
    if (!checkoutPlan || !couponDraft.trim()) return;
    setValidatingCoupon(true);
    setCouponError(null);
    try {
      const res = await validateCheckoutCoupon({
        code: couponDraft.trim(),
        plan_id: checkoutPlan.id,
      });
      if (!res?.success || !res.data) {
        setCouponPreview(null);
        setCouponError(t("settings.subscription.checkout.coupon.invalid"));
        return;
      }
      setCouponPreview(res.data);
      setCouponDraft(res.data.code);
    } catch (e: any) {
      setCouponPreview(null);
      setCouponError(
        e?.message || t("settings.subscription.checkout.coupon.invalid")
      );
    } finally {
      setValidatingCoupon(false);
    }
  };

  const clearAppliedCoupon = () => {
    setCouponPreview(null);
    setCouponError(null);
    setCouponDraft("");
  };

  const runCheckout = async (cardTokenId: string) => {
    if (!checkoutPlan || !canConfirmCheckout) return;
    setCheckingOut(true);
    try {
      const res = await checkoutSubscription({
        plan_id: checkoutPlan.id,
        card_token_id: cardTokenId,
        coupon_code: couponPreview?.code || undefined,
      });

      // USD PayPal: redirecionar para approval_url se retornado.
      if (res.data?.approval_url) {
        window.location.href = res.data.approval_url;
        return;
      }

      toast.success(
        res.message || t("settings.subscription.checkout.success"),
        { autoClose: 3500 }
      );
      setCheckoutPlan(null);
      resetCouponState();
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("settings.subscription.checkout.error"), {
        autoClose: 5000,
      });
      throw e;
    } finally {
      setCheckingOut(false);
    }
  };

  const confirmFakeCheckout = async () => {
    if (!canConfirmCheckout) return;
    try {
      await runCheckout(FAKE_CARD_TOKEN_ID);
    } catch {
      /* toast já exibido */
    }
  };

  const runCancel = async () => {
    setCancelling(true);
    try {
      const res = await cancelSubscription({ when: cancelWhen });
      toast.success(
        res.message || t("settings.subscription.cancel.success"),
        { autoClose: 3500 }
      );
      setCancelOpen(false);
      if (res.data?.status === "pending_cancel") {
        setSubscription(res.data);
        return;
      }
      // Soft-lock: envia Carla para /suspended (DOC 09) em vez de deixar na página operacional.
      navigate("/suspended", { replace: true });
    } catch (e: any) {
      toast.error(e?.message || t("settings.subscription.cancel.error"), {
        autoClose: 5000,
      });
    } finally {
      setCancelling(false);
    }
  };

  const runResume = async () => {
    setResuming(true);
    try {
      const res = await resumeSubscription();
      toast.success(
        res.message || t("settings.subscription.resume.success"),
        { autoClose: 3500 }
      );
      if (res.data) {
        setSubscription(res.data);
      } else {
        await load();
      }
    } catch (e: any) {
      toast.error(e?.message || t("settings.subscription.resume.error"), {
        autoClose: 5000,
      });
    } finally {
      setResuming(false);
    }
  };

  const runChangeCard = async (cardTokenId: string) => {
    setChangingCard(true);
    try {
      const res = await updatePaymentMethod({ card_token_id: cardTokenId });
      toast.success(
        res.message || t("settings.subscription.changeCard.success"),
        { autoClose: 3500 }
      );
      setChangeCardOpen(false);
      if (res.data) {
        setSubscription(res.data);
      } else {
        await load();
      }
    } catch (e: any) {
      toast.error(e?.message || t("settings.subscription.changeCard.error"), {
        autoClose: 5000,
      });
      throw e;
    } finally {
      setChangingCard(false);
    }
  };

  const runPackPurchase = async (
    cardTokenId?: string,
    useSavedCard = false
  ) => {
    setBuyingPack(true);
    try {
      const res = await purchaseQuotaPack(
        useSavedCard
          ? { use_saved_card: true }
          : { card_token_id: cardTokenId || "" }
      );

      // USD PayPal: redirecionar para approval_url se retornado (pack também pode usar PayPal).
      if (res.data?.approval_url) {
        window.location.href = res.data.approval_url;
        return;
      }

      toast.success(
        res.message || t("settings.subscription.addon.success"),
        { autoClose: 3500 }
      );
      setPackOpen(false);
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("settings.subscription.addon.error"), {
        autoClose: 5000,
      });
      throw e;
    } finally {
      setBuyingPack(false);
    }
  };

  const confirmFakePack = async () => {
    try {
      await runPackPurchase(FAKE_CARD_TOKEN_ID);
    } catch {
      /* toast já exibido */
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("settings.subscription.pageTitle")}
          actions={
            <PageHelpLink
              href={helpHref}
              label={t("settings.subscription.help")}
            />
          }
        />
        <ToastContainer closeButton={false} limit={3} />

        {loading && (
          <Row>
            <Col lg={8}>
              <Card>
                <CardBody className="placeholder-glow">
                  <span className="placeholder col-4 mb-3" />
                  <span className="placeholder col-12 mb-2" />
                  <span className="placeholder col-8 mb-2" />
                  <span className="placeholder col-12" />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {!loading && unavailable && (
          <Card>
            <CardBody className="text-center py-5">
              <h5 className="mb-2">{t("settings.subscription.empty.title")}</h5>
              <p className="text-muted mb-3">
                {t("settings.subscription.empty.description")}
              </p>
              <Link to="/dashboard" className="btn btn-light">
                {t("settings.subscription.empty.back")}
              </Link>
            </CardBody>
          </Card>
        )}

        {!loading && subscription && plan && (
          <Row>
            <Col lg={10}>
              {awaitingFirstCheckout ? (
                <Alert color="info" className="mb-3">
                  <div className="fw-medium mb-1">
                    {t("settings.subscription.registerCheckout.title")}
                  </div>
                  <p className="mb-0">
                    {t("settings.subscription.registerCheckout.body")}
                  </p>
                </Alert>
              ) : null}

              {((isPending && !awaitingFirstCheckout) || hasOpenPackInvoice) && (
                <Alert color="warning" className="mb-3">
                  <div className="fw-medium mb-1">
                    {hasOpenPackInvoice && !isPending
                      ? t("settings.subscription.packPendingNotice")
                      : t("settings.subscription.pendingNotice")}
                  </div>
                  <div className="small mb-2">
                    {t("settings.subscription.pendingCountdown", {
                      seconds: pendingCountdown,
                    })}
                  </div>
                  <Button
                    color="warning"
                    outline
                    size="sm"
                    onClick={() => {
                      setPendingCountdown(PENDING_POLL_SECONDS);
                      void load({ silent: true });
                    }}
                  >
                    {t("settings.subscription.pendingRefresh")}
                  </Button>
                </Alert>
              )}

              {externallyBilled && (
                <Alert color="info" className="mb-3">
                  {t("settings.subscription.managedByBrdominio")}
                </Alert>
              )}

              {/* Promo ainda em vigor — Carla sabe quantas faturas restam (LAC-089). */}
              {subscription.active_coupon && (
                <Alert color="success" className="mb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <span className="fw-medium">
                      {t("settings.subscription.activeCoupon.title")}
                    </span>
                    <Badge color="success" pill>
                      {t("settings.subscription.activeCoupon.badge")}
                    </Badge>
                  </div>
                  <p className="mb-2 mb-md-0">
                    {activeCouponStatusMessage(
                      subscription.active_coupon,
                      t,
                      typeof subscription.active_coupon.amount_cents ===
                        "number"
                        ? formatCents(
                            subscription.active_coupon.amount_cents,
                            currency,
                            locale
                          )
                        : null
                    )}
                  </p>
                  <a
                    href={couponHelpHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small fw-medium"
                  >
                    {t("settings.subscription.activeCoupon.helpLink")}
                    <i className="ri-external-link-line ms-1 align-middle" />
                  </a>
                </Alert>
              )}

              <Card>
                <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <h5 className="card-title mb-0">
                    {t("settings.subscription.planCard")}
                  </h5>
                  <div className="d-flex flex-wrap gap-1">
                    {subscription.active_coupon && (
                      <Badge color="success">
                        {t("settings.subscription.activeCoupon.badge")}
                      </Badge>
                    )}
                    <Badge color={statusBadgeColor(subscription.status)}>
                      {t(`settings.subscription.status.${subscription.status}`, {
                        defaultValue: subscription.status,
                      })}
                    </Badge>
                  </div>
                </CardHeader>
                <CardBody>
                  <div
                    id="subscription-payment-focus"
                    className={paymentFocus ? "rounded border border-warning p-2 mb-2" : undefined}
                  >
                  {/* LAC-100: past_due pode ser atraso real ou webhook ainda confirmando. */}
                  {subscription.status === "past_due" ? (
                    <Alert color="warning" className="py-2">
                      <p className="mb-1">{t("settings.subscription.pastDueHint")}</p>
                      {typeof subscription.days_until_suspend === "number" ? (
                        <p className="mb-0 fw-medium">
                          {subscription.days_until_suspend <= 0
                            ? t("dashboard.billing.pastDue.countdownToday")
                            : t("dashboard.billing.pastDue.countdown", {
                                days: subscription.days_until_suspend,
                              })}
                        </p>
                      ) : null}
                    </Alert>
                  ) : null}
                  {subscription.status === "pending_cancel" ? (
                    <Alert color="info" className="py-2">
                      <p className="mb-1">
                        {t("settings.subscription.pendingCancelHint", {
                          end: periodEnd || "—",
                        })}
                      </p>
                      {typeof subscription.days_until_period_end === "number" ? (
                        <p className="mb-0 fw-medium">
                          {subscription.days_until_period_end <= 0
                            ? t("settings.subscription.pendingCancel.countdownToday")
                            : t("settings.subscription.pendingCancel.countdown", {
                                days: subscription.days_until_period_end,
                              })}
                        </p>
                      ) : null}
                    </Alert>
                  ) : null}
                  {/* LAC-197: suspended — conta pausada, CTA implícito nesta tela. */}
                  {subscription.status === "suspended" ? (
                    <Alert color="danger" className="py-2">
                      {t("settings.subscription.suspendedHint")}
                    </Alert>
                  ) : null}
                  </div>
                  <h4 className="mb-1">{plan.name}</h4>
                  {plan.is_trial_plan && (
                    <Badge color="info" className="mb-2">
                      {t("settings.subscription.trialBadge")}
                    </Badge>
                  )}
                  <p className="text-muted mb-3">
                    {typeof plan.base_price_cents === "number"
                      ? formatCents(plan.base_price_cents, currency, locale)
                      : "—"}
                    {intervalKey ? ` · ${t(intervalKey)}` : null}
                    {typeof plan.base_price_cents === "number" &&
                    (plan.email_quota || 0) > 0 ? (
                      <span className="d-block small mt-1">
                        {t("settings.subscription.pricePerThousand", {
                          amount: formatCentsPerThousand(
                            plan.base_price_cents,
                            plan.email_quota || 0,
                            currency,
                            locale
                          ),
                        })}
                      </span>
                    ) : null}
                  </p>

                  <h6 className="mb-2">{t("settings.subscription.quotaTitle")}</h6>
                  <p className="mb-2">
                    {t("dashboard.quota.usage", {
                      used: used.toLocaleString(locale),
                      quota: quota.toLocaleString(locale),
                    })}
                  </p>
                  {packs > 0 && (
                    <p className="small text-muted mb-2">
                      {t("settings.subscription.packsSummary", {
                        packs,
                        emails: (
                          packs * QUOTA_PACK_EMAILS
                        ).toLocaleString(locale),
                      })}
                    </p>
                  )}
                  <Progress
                    value={percent}
                    color={progressColor}
                    className="progress-sm mb-2"
                  />
                  <div className="d-flex justify-content-between small text-muted mb-3">
                    <span>
                      {t("dashboard.quota.remaining", {
                        remaining: remaining.toLocaleString(locale),
                      })}
                    </span>
                    <span>{percent}%</span>
                  </div>

                  {(periodStart || periodEnd) && (
                    <Alert color="light" className="mb-3 border">
                      <div className="fw-medium mb-1">
                        {t("settings.subscription.cycleTitle")}
                      </div>
                      <div className="small text-muted mb-0">
                        {t("settings.subscription.cycleRange", {
                          start: periodStart || "—",
                          end: periodEnd || "—",
                          used: used.toLocaleString(locale),
                        })}
                      </div>
                    </Alert>
                  )}

                  {exhausted && (
                    <Alert color="danger" className="mb-3">
                      {t("dashboard.quota.exhausted")}
                    </Alert>
                  )}
                  {!exhausted && nearLimit && (
                    <Alert color="warning" className="mb-3">
                      {t("dashboard.quota.nearLimit")}
                    </Alert>
                  )}

                  {canBuyPack && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {subscription.has_saved_card && currency !== "USD" ? (
                        <Button
                          color="success"
                          disabled={busy}
                          onClick={() => setOneClickOpen(true)}
                        >
                          {t("settings.subscription.addon.oneClickCta", {
                            brand:
                              subscription.saved_card?.brand_label ||
                              t("settings.subscription.addon.cardFallback"),
                            last4: subscription.saved_card?.last4 || "****",
                            emails: QUOTA_PACK_EMAILS.toLocaleString(locale),
                            price: formatCents(
                              QUOTA_PACK_PRICE_CENTS,
                              currency,
                              locale
                            ),
                          })}
                        </Button>
                      ) : null}
                      <Button
                        color={
                          subscription.has_saved_card && currency !== "USD"
                            ? "light"
                            : "success"
                        }
                        outline={
                          !!(subscription.has_saved_card && currency !== "USD")
                        }
                        disabled={busy}
                        onClick={() => setPackOpen(true)}
                      >
                        {subscription.has_saved_card && currency !== "USD"
                          ? t("settings.subscription.addon.otherCardCta")
                          : t("settings.subscription.addon.cta", {
                              emails: QUOTA_PACK_EMAILS.toLocaleString(locale),
                              price: formatCents(
                                currency === "USD"
                                  ? QUOTA_PACK_USD_PRICE_CENTS
                                  : QUOTA_PACK_PRICE_CENTS,
                                currency,
                                locale
                              ),
                            })}
                      </Button>
                      <span className="align-self-center small text-muted">
                        {t("settings.subscription.addon.remainingPacks", {
                          remaining: QUOTA_PACK_MAX_PER_PERIOD - packs,
                        })}
                      </span>
                    </div>
                  )}

                  {packsMaxReached && (
                    <Alert color="info" className="mb-3">
                      <p className="mb-2 mb-md-0">
                        {t("settings.subscription.addon.maxReached")}
                      </p>
                      <a href="#upgrade-plans" className="btn btn-sm btn-primary">
                        {t("settings.subscription.addon.upgradeCta")}
                      </a>
                    </Alert>
                  )}

                  {canManageBilling &&
                    !isPending &&
                    subscription.status !== "pending_cancel" && (
                    <div className="border-top pt-3 d-flex flex-wrap gap-2">
                      {subscription.has_saved_card && currency !== "USD" ? (
                        <Button
                          color="light"
                          outline
                          size="sm"
                          disabled={busy}
                          onClick={() => setChangeCardOpen(true)}
                        >
                          {t("settings.subscription.changeCard.cta", {
                            brand:
                              subscription.saved_card?.brand_label ||
                              t("settings.subscription.addon.cardFallback"),
                            last4: subscription.saved_card?.last4 || "****",
                          })}
                        </Button>
                      ) : null}
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setCancelWhen("period_end");
                          setCancelOpen(true);
                        }}
                      >
                        {t("settings.subscription.cancel.cta")}
                      </Button>
                    </div>
                  )}
                  {subscription.status === "pending_cancel" && canManageBilling && (
                    <div className="border-top pt-3 d-flex flex-wrap gap-2">
                      <Button
                        color="success"
                        size="sm"
                        disabled={busy}
                        onClick={() => void runResume()}
                      >
                        {resuming ? (
                          <>
                            <Spinner size="sm" className="me-1" />
                            {t("settings.subscription.checkout.processing")}
                          </>
                        ) : (
                          t("settings.subscription.resume.cta")
                        )}
                      </Button>
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setCancelWhen("now");
                          setCancelOpen(true);
                        }}
                      >
                        {t("settings.subscription.cancel.nowAnywayCta")}
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              {!externallyBilled && (
              <>
                {/* Currency toggle: BRL sempre; USD se ready, senão empty state EN/intl. */}
                <Card className="mb-3">
                  <CardBody>
                    <h6 className="mb-3">{t("settings.subscription.currency.title")}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        color={catalogCurrency === "BRL" ? "primary" : "light"}
                        outline={catalogCurrency !== "BRL"}
                        onClick={() => setCatalogCurrency("BRL")}
                        disabled={checkoutLocked || busy}
                      >
                        {t("settings.subscription.currency.brl")}
                      </Button>
                      <Button
                        color={catalogCurrency === "USD" ? "primary" : "light"}
                        outline={catalogCurrency !== "USD"}
                        onClick={() => usdReady && setCatalogCurrency("USD")}
                        disabled={checkoutLocked || busy || !usdReady}
                      >
                        {t("settings.subscription.currency.usd")}
                      </Button>
                    </div>
                    {usdReady ? (
                      <div className="form-text mt-2">
                        {t("settings.subscription.currency.hint")}{" "}
                        <a href={paypalHelpHref} target="_blank" rel="noreferrer">
                          {t("settings.subscription.currency.helpLink")}
                        </a>
                        {" · "}
                        <a
                          href="https://brsend.com/en/compare/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t("settings.subscription.currency.compareLink")}
                        </a>
                      </div>
                    ) : (
                      <Alert color="light" className="border mt-3 mb-0">
                        {t("settings.subscription.currency.usdUnavailable")}
                      </Alert>
                    )}
                  </CardBody>
                </Card>

              <Card id="upgrade-plans">
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("settings.subscription.upgrade.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  <p className="text-muted mb-3">
                    {t("settings.subscription.upgrade.description")}
                  </p>
                  {checkoutLocked && (
                    <Alert color="warning" className="mb-3">
                      {t("settings.subscription.upgrade.disabledPending")}
                    </Alert>
                  )}
                  {upgradePlans.length === 0 ? (
                    <p className="text-muted mb-0">
                      {t("settings.subscription.upgrade.empty")}
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("settings.subscription.upgrade.colPlan")}</th>
                            <th>{t("settings.subscription.upgrade.colQuota")}</th>
                            <th>{t("settings.subscription.upgrade.colPrice")}</th>
                            <th className="text-end">
                              {t("settings.subscription.upgrade.colAction")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {upgradePlans.map((p) => {
                            const planCurrency = p.currency || "BRL";
                            const planInterval = p.billing_interval
                              ? t(
                                  `settings.subscription.interval.${p.billing_interval}`
                                )
                              : null;
                            const wouldDowngrade =
                              (p.email_quota || 0) < (plan.email_quota || 0) &&
                              p.id !== plan.id;
                            return (
                              <tr key={p.id}>
                                <td>
                                  <div className="fw-medium">{p.name}</div>
                                  {p.id === plan.id && awaitingFirstCheckout ? (
                                    <Badge color="info" className="mt-1 me-1">
                                      {t(
                                        "settings.subscription.registerCheckout.suggested"
                                      )}
                                    </Badge>
                                  ) : null}
                                  {p.description ? (
                                    <div className="small text-muted">
                                      {p.description}
                                    </div>
                                  ) : null}
                                  {wouldDowngrade ? (
                                    <Badge color="soft-warning" className="mt-1">
                                      {t(
                                        "settings.subscription.upgrade.downgradeBadge"
                                      )}
                                    </Badge>
                                  ) : null}
                                </td>
                                <td>
                                  {t("settings.subscription.upgrade.quotaValue", {
                                    quota: (p.email_quota || 0).toLocaleString(
                                      locale
                                    ),
                                  })}
                                </td>
                                <td>
                                  {typeof p.base_price_cents === "number"
                                    ? formatCents(
                                        p.base_price_cents,
                                        planCurrency,
                                        locale
                                      )
                                    : "—"}
                                  {planInterval ? (
                                    <span className="text-muted small">
                                      {" "}
                                      · {planInterval}
                                    </span>
                                  ) : null}
                                  {typeof p.base_price_cents === "number" &&
                                  (p.email_quota || 0) > 0 ? (
                                    <div className="small text-muted">
                                      {t(
                                        "settings.subscription.pricePerThousand",
                                        {
                                          amount: formatCentsPerThousand(
                                            p.base_price_cents,
                                            p.email_quota || 0,
                                            planCurrency,
                                            locale
                                          ),
                                        }
                                      )}
                                    </div>
                                  ) : null}
                                </td>
                                <td className="text-end">
                                  <Button
                                    color={wouldDowngrade ? "warning" : "primary"}
                                    size="sm"
                                    disabled={checkoutLocked || busy}
                                    onClick={() => openCheckout(p)}
                                  >
                                    {wouldDowngrade
                                      ? t(
                                          "settings.subscription.upgrade.selectDowngrade"
                                        )
                                      : t(
                                          "settings.subscription.upgrade.select"
                                        )}
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
              </>
              )}

              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">
                    {t("settings.subscription.invoices.title")}
                  </h5>
                </CardHeader>
                <CardBody>
                  {invoices.length === 0 ? (
                    <p className="text-muted mb-0">
                      {t("settings.subscription.invoices.empty")}
                    </p>
                  ) : (
                    <div className="table-responsive">
                      <Table className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>
                              {t("settings.subscription.invoices.colDate")}
                            </th>
                            <th>
                              {t("settings.subscription.invoices.colAmount")}
                            </th>
                            <th>
                              {t("settings.subscription.invoices.colKind")}
                            </th>
                            <th>
                              {t("settings.subscription.invoices.colCoupon")}
                            </th>
                            <th>
                              {t("settings.subscription.invoices.colStatus")}
                            </th>
                            <th>
                              {t("settings.subscription.invoices.colGateway")}
                            </th>
                            <th className="text-end">
                              {t("settings.subscription.invoices.colReceipt")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((inv) => {
                            const dateLabel =
                              formatDate(inv.paid_at) ||
                              formatDate(inv.created_at) ||
                              "—";
                            const kind = inv.kind || "subscription";
                            const gatewayLabel =
                              inv.gateway_label ||
                              (inv.currency === "USD" ? "PayPal" : "Mercado Pago");
                            return (
                              <tr key={inv.id}>
                                <td>{dateLabel}</td>
                                <td>
                                  {formatCents(
                                    inv.amount_cents,
                                    inv.currency || "BRL",
                                    locale
                                  )}
                                </td>
                                <td>
                                  {t(
                                    `settings.subscription.invoices.kind.${kind}`,
                                    { defaultValue: kind }
                                  )}
                                </td>
                                <td className="text-muted">
                                  {inv.coupon_code || "—"}
                                </td>
                                <td>
                                  <Badge color={statusBadgeColor(inv.status)}>
                                    {t(
                                      `settings.subscription.invoices.status.${inv.status}`,
                                      { defaultValue: inv.status }
                                    )}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge
                                    color={
                                      inv.currency === "USD"
                                        ? "info"
                                        : "secondary"
                                    }
                                    pill
                                  >
                                    {gatewayLabel}
                                  </Badge>
                                </td>
                                <td className="text-end">
                                  {inv.status === "paid" ? (
                                    <div className="d-inline-flex flex-wrap gap-2 justify-content-end">
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={async () => {
                                          try {
                                            await openInvoiceReceipt(inv.id);
                                          } catch (err: any) {
                                            toast.error(
                                              err?.message ||
                                                t(
                                                  "settings.subscription.invoices.receiptError"
                                                )
                                            );
                                          }
                                        }}
                                      >
                                        {t(
                                          "settings.subscription.invoices.receiptOpen"
                                        )}
                                      </Button>
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={async () => {
                                          try {
                                            await downloadInvoiceReceipt(inv.id);
                                          } catch (err: any) {
                                            toast.error(
                                              err?.message ||
                                                t(
                                                  "settings.subscription.invoices.receiptError"
                                                )
                                            );
                                          }
                                        }}
                                      >
                                        {t(
                                          "settings.subscription.invoices.receiptDownload"
                                        )}
                                      </Button>
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0"
                                        onClick={async () => {
                                          try {
                                            const res =
                                              await resendInvoiceReceipt(inv.id);
                                            toast.success(
                                              res?.message ||
                                                t(
                                                  "settings.subscription.invoices.receiptResent"
                                                ),
                                              { autoClose: 3000 }
                                            );
                                          } catch (err: any) {
                                            toast.error(
                                              err?.message ||
                                                t(
                                                  "settings.subscription.invoices.receiptError"
                                                )
                                            );
                                          }
                                        }}
                                      >
                                        {t(
                                          "settings.subscription.invoices.receiptResend"
                                        )}
                                      </Button>
                                    </div>
                                  ) : (
                                    <span className="text-muted">—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Checkout / upgrade */}
        <Modal
          isOpen={!!checkoutPlan}
          toggle={() => (!checkingOut ? setCheckoutPlan(null) : undefined)}
          centered
          size={useBrick ? "lg" : undefined}
        >
          <ModalHeader
            toggle={() => (!checkingOut ? setCheckoutPlan(null) : undefined)}
          >
            {isDowngrade
              ? t("settings.subscription.checkout.downgradeTitle")
              : t("settings.subscription.checkout.title")}
          </ModalHeader>
          <ModalBody>
            {checkoutPlan && plan && (
              <>
                <p className="mb-2">
                  {t("settings.subscription.checkout.confirm", {
                    plan: checkoutPlan.name,
                    price: formatCents(
                      checkoutPlan.base_price_cents || 0,
                      checkoutPlan.currency || catalogCurrency,
                      locale
                    ),
                    quota: (checkoutPlan.email_quota || 0).toLocaleString(
                      locale
                    ),
                  })}
                </p>
                {typeof checkoutPlan.base_price_cents === "number" &&
                (checkoutPlan.email_quota || 0) > 0 ? (
                  <p className="small text-muted mb-2">
                    {t("settings.subscription.pricePerThousand", {
                      amount: formatCentsPerThousand(
                        checkoutPlan.base_price_cents,
                        checkoutPlan.email_quota || 0,
                        checkoutPlan.currency || catalogCurrency,
                        locale
                      ),
                    })}
                  </p>
                ) : null}
                <Alert color="light" className="border small mb-3">
                  {t("settings.subscription.checkout.quotaPreview", {
                    current: quota.toLocaleString(locale),
                    next: (checkoutPlan.email_quota || 0).toLocaleString(
                      locale
                    ),
                  })}
                </Alert>

                <FormGroup check className="mb-2">
                  <Input
                    id="checkout-coupon-toggle"
                    type="checkbox"
                    checked={couponEnabled}
                    disabled={checkingOut || validatingCoupon}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setCouponEnabled(on);
                      if (!on) {
                        clearAppliedCoupon();
                      }
                    }}
                  />
                  <Label
                    check
                    htmlFor="checkout-coupon-toggle"
                    className="ms-1"
                  >
                    {t("settings.subscription.checkout.coupon.toggle")}
                  </Label>
                  <a
                    href={couponHelpHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small ms-2"
                  >
                    {t("settings.subscription.checkout.coupon.helpLink")}
                  </a>
                </FormGroup>

                {couponEnabled ? (
                  <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2 align-items-start">
                      <Input
                        bsSize="sm"
                        value={couponDraft}
                        disabled={
                          checkingOut ||
                          validatingCoupon ||
                          !!couponPreview
                        }
                        onChange={(e) =>
                          setCouponDraft(
                            e.target.value.toUpperCase().replace(/\s+/g, "")
                          )
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            void applyCoupon();
                          }
                        }}
                        placeholder={t(
                          "settings.subscription.checkout.coupon.placeholder"
                        )}
                        aria-label={t(
                          "settings.subscription.checkout.coupon.code"
                        )}
                        style={{ maxWidth: 220 }}
                      />
                      {couponPreview ? (
                        <Button
                          color="light"
                          size="sm"
                          disabled={checkingOut}
                          onClick={clearAppliedCoupon}
                        >
                          {t("settings.subscription.checkout.coupon.change")}
                        </Button>
                      ) : (
                        <Button
                          color="primary"
                          outline
                          size="sm"
                          disabled={
                            checkingOut ||
                            validatingCoupon ||
                            !couponDraft.trim()
                          }
                          onClick={() => void applyCoupon()}
                        >
                          {validatingCoupon ? (
                            <>
                              <Spinner size="sm" className="me-1" />
                              {t(
                                "settings.subscription.checkout.coupon.applying"
                              )}
                            </>
                          ) : (
                            t("settings.subscription.checkout.coupon.apply")
                          )}
                        </Button>
                      )}
                    </div>
                    {couponError ? (
                      <div className="text-danger small mt-1">{couponError}</div>
                    ) : null}
                    {couponPreview ? (
                      <Alert color="success" className="border small mt-2 mb-0">
                        <div className="fw-medium mb-1">
                          {t(
                            "settings.subscription.checkout.coupon.applied",
                            { code: couponPreview.code }
                          )}
                        </div>
                        <div>
                          {t(
                            "settings.subscription.checkout.coupon.breakdown",
                            {
                              subtotal: formatCents(
                                couponPreview.subtotal_cents,
                                couponPreview.currency || "BRL",
                                locale
                              ),
                              discount: formatCents(
                                couponPreview.discount_cents,
                                couponPreview.currency || "BRL",
                                locale
                              ),
                              total: formatCents(
                                couponPreview.total_cents,
                                couponPreview.currency || "BRL",
                                locale
                              ),
                            }
                          )}
                        </div>
                        <div className="text-muted mt-1">
                          {t(
                            `settings.subscription.checkout.coupon.duration.${couponPreview.duration_type}`,
                            {
                              count: couponPreview.duration_cycles ?? 0,
                              defaultValue: couponPreview.duration_type,
                            }
                          )}
                        </div>
                      </Alert>
                    ) : null}
                  </div>
                ) : null}

                {isDowngrade && (
                  <Alert color="warning" className="mb-3">
                    <p className="mb-2">
                      {t("settings.subscription.checkout.downgradeWarning", {
                        currentQuota: (plan.email_quota || 0).toLocaleString(
                          locale
                        ),
                        newQuota: (checkoutPlan.email_quota || 0).toLocaleString(
                          locale
                        ),
                      })}
                    </p>
                    <FormGroup check className="mb-0">
                      <Input
                        id="downgrade-ack"
                        type="checkbox"
                        checked={downgradeAck}
                        disabled={checkingOut}
                        onChange={(e) => setDowngradeAck(e.target.checked)}
                      />
                      <Label check htmlFor="downgrade-ack" className="ms-1">
                        {t("settings.subscription.checkout.downgradeAck")}
                      </Label>
                    </FormGroup>
                  </Alert>
                )}

                {checkoutPlan.currency === "USD" ? (
                  canConfirmCheckout ? (
                    <PayPalSubscribeButton
                      clientId={paypalClientId}
                      planId={checkoutPlan.id}
                      disabled={checkingOut}
                      label={
                        checkingOut
                          ? t("settings.subscription.checkout.processing")
                          : t("settings.subscription.checkout.paypalCta")
                      }
                      onPrepare={async () => {
                        const res = await preparePayPalCheckout({
                          plan_id: checkoutPlan.id,
                          coupon_code: couponPreview?.code || undefined,
                        });
                        if (!res?.success || !res.data?.paypal_plan_id) {
                          throw new Error(
                            t("settings.subscription.checkout.error")
                          );
                        }
                        return res.data;
                      }}
                      onApproved={async (subscriptionId) => {
                        await runCheckout(subscriptionId);
                      }}
                      onPayFallback={async () => {
                        try {
                          await runCheckout("paypal-approve");
                        } catch {
                          /* toast */
                        }
                      }}
                      onError={(msg) =>
                        toast.error(msg, { autoClose: 5000 })
                      }
                    />
                  ) : (
                    <Alert color="light" className="border mb-0">
                      {t("settings.subscription.checkout.brickNeedAck")}
                    </Alert>
                  )
                ) : useBrick ? (
                  canConfirmCheckout ? (
                    <MercadoPagoCardBrick
                      key={`checkout-brick-${checkoutAmountCents}`}
                      amountReais={checkoutAmountCents / 100}
                      disabled={checkingOut}
                      onToken={runCheckout}
                      onError={(msg) => toast.error(msg, { autoClose: 5000 })}
                    />
                  ) : (
                    <Alert color="light" className="border mb-0">
                      {t("settings.subscription.checkout.brickNeedAck")}
                    </Alert>
                  )
                ) : (
                  <Alert color="info" className="mb-0">
                    {t("settings.subscription.checkout.fakeNotice")}
                  </Alert>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={checkingOut}
              onClick={() => {
                setCheckoutPlan(null);
                resetCouponState();
              }}
            >
              {t("settings.subscription.checkout.cancel")}
            </Button>
            {!useBrick && checkoutPlan?.currency !== "USD" && (
              <Button
                color={isDowngrade ? "warning" : "primary"}
                disabled={checkingOut || !canConfirmCheckout}
                onClick={confirmFakeCheckout}
              >
                {checkingOut ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    {t("settings.subscription.checkout.processing")}
                  </>
                ) : isDowngrade ? (
                  t("settings.subscription.checkout.confirmDowngradeCta")
                ) : (
                  t("settings.subscription.checkout.confirmCta")
                )}
              </Button>
            )}
          </ModalFooter>
        </Modal>

        {/* Pack +10k */}
        <Modal
          isOpen={packOpen}
          toggle={() => (!buyingPack ? setPackOpen(false) : undefined)}
          centered
          size={useBrick ? "lg" : undefined}
        >
          <ModalHeader
            toggle={() => (!buyingPack ? setPackOpen(false) : undefined)}
          >
            {t("settings.subscription.addon.title")}
          </ModalHeader>
          <ModalBody>
            <p className="mb-2">
              {t("settings.subscription.addon.confirm", {
                emails: QUOTA_PACK_EMAILS.toLocaleString(locale),
                price: formatCents(
                  currency === "USD"
                    ? QUOTA_PACK_USD_PRICE_CENTS
                    : QUOTA_PACK_PRICE_CENTS,
                  currency,
                  locale
                ),
                remaining: QUOTA_PACK_MAX_PER_PERIOD - packs,
              })}
            </p>
            <Alert color="light" className="border small mb-3">
              {t("settings.subscription.addon.quotaPreview", {
                current: quota.toLocaleString(locale),
                next: packPreviewQuota.toLocaleString(locale),
              })}
            </Alert>
            {currency === "USD" ? (
              <PayPalSubscribeButton
                clientId={null}
                planId={0}
                disabled={buyingPack}
                label={
                  buyingPack
                    ? t("settings.subscription.checkout.processing")
                    : t("settings.subscription.addon.paypalCta")
                }
                onPrepare={async () => ({ paypal_plan_id: "P-PACK" })}
                onApproved={async () => undefined}
                onPayFallback={async () => {
                  try {
                    await runPackPurchase("paypal-approve");
                  } catch {
                    /* toast */
                  }
                }}
                onError={(msg) => toast.error(msg, { autoClose: 5000 })}
              />
            ) : useBrick ? (
              <MercadoPagoCardBrick
                amountReais={QUOTA_PACK_PRICE_CENTS / 100}
                disabled={buyingPack}
                onToken={runPackPurchase}
                onError={(msg) => toast.error(msg, { autoClose: 5000 })}
              />
            ) : (
              <Alert color="info" className="mb-0">
                {t("settings.subscription.checkout.fakeNotice")}
              </Alert>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={buyingPack}
              onClick={() => setPackOpen(false)}
            >
              {t("settings.subscription.checkout.cancel")}
            </Button>
            {!useBrick && (
              <Button
                color="success"
                disabled={buyingPack}
                onClick={confirmFakePack}
              >
                {buyingPack ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    {t("settings.subscription.checkout.processing")}
                  </>
                ) : (
                  t("settings.subscription.addon.confirmCta")
                )}
              </Button>
            )}
          </ModalFooter>
        </Modal>

        {/* 1-click pack — confirma valor antes de cobrar o cartão salvo. */}
        <Modal
          isOpen={oneClickOpen}
          toggle={() => (!buyingPack ? setOneClickOpen(false) : undefined)}
          centered
        >
          <ModalHeader
            toggle={() => (!buyingPack ? setOneClickOpen(false) : undefined)}
          >
            {t("settings.subscription.addon.oneClickTitle")}
          </ModalHeader>
          <ModalBody>
            <p className="mb-2">
              {t("settings.subscription.addon.oneClickConfirm", {
                brand:
                  subscription?.saved_card?.brand_label ||
                  t("settings.subscription.addon.cardFallback"),
                last4: subscription?.saved_card?.last4 || "****",
                emails: QUOTA_PACK_EMAILS.toLocaleString(locale),
                price: formatCents(
                  QUOTA_PACK_PRICE_CENTS,
                  currency,
                  locale
                ),
              })}
            </p>
            <p className="small text-muted mb-0">
              {t("settings.subscription.addon.quotaPreview", {
                current: quota.toLocaleString(locale),
                next: packPreviewQuota.toLocaleString(locale),
              })}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={buyingPack}
              onClick={() => setOneClickOpen(false)}
            >
              {t("settings.subscription.checkout.cancel")}
            </Button>
            <Button
              color="success"
              disabled={buyingPack}
              onClick={async () => {
                try {
                  await runPackPurchase(undefined, true);
                  setOneClickOpen(false);
                } catch {
                  /* toast já exibido */
                }
              }}
            >
              {buyingPack ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {t("settings.subscription.checkout.processing")}
                </>
              ) : (
                t("settings.subscription.addon.confirmCta")
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Trocar cartão da recorrência. */}
        <Modal
          isOpen={changeCardOpen}
          toggle={() => (!changingCard ? setChangeCardOpen(false) : undefined)}
          centered
          size="lg"
        >
          <ModalHeader
            toggle={() => (!changingCard ? setChangeCardOpen(false) : undefined)}
          >
            {t("settings.subscription.changeCard.title")}
          </ModalHeader>
          <ModalBody>
            <Alert color="light" className="border mb-3">
              {t("settings.subscription.changeCard.hint")}
            </Alert>
            {useBrick ? (
              <MercadoPagoCardBrick
                amountReais={Math.max(
                  1,
                  (plan?.base_price_cents || QUOTA_PACK_PRICE_CENTS) / 100
                )}
                onToken={runChangeCard}
                disabled={changingCard}
              />
            ) : (
              <Button
                color="primary"
                disabled={changingCard}
                onClick={() => void runChangeCard(FAKE_CARD_TOKEN_ID)}
              >
                {changingCard ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    {t("settings.subscription.checkout.processing")}
                  </>
                ) : (
                  t("settings.subscription.changeCard.fakeCta")
                )}
              </Button>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={changingCard}
              onClick={() => setChangeCardOpen(false)}
            >
              {t("settings.subscription.checkout.cancel")}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Cancel — agora ou fim do ciclo (LAC-056). */}
        <Modal
          isOpen={cancelOpen}
          toggle={() => (!cancelling ? setCancelOpen(false) : undefined)}
          centered
        >
          <ModalHeader
            toggle={() => (!cancelling ? setCancelOpen(false) : undefined)}
          >
            {t("settings.subscription.cancel.title")}
          </ModalHeader>
          <ModalBody>
            {subscription?.status !== "pending_cancel" ? (
              <FormGroup tag="fieldset" className="mb-3">
                <FormGroup check className="mb-2">
                  <Input
                    type="radio"
                    name="cancelWhen"
                    id="cancelWhenPeriodEnd"
                    checked={cancelWhen === "period_end"}
                    disabled={cancelling}
                    onChange={() => setCancelWhen("period_end")}
                  />
                  <Label check for="cancelWhenPeriodEnd">
                    {t("settings.subscription.cancel.whenPeriodEnd", {
                      end: periodEnd || "—",
                    })}
                  </Label>
                </FormGroup>
                <FormGroup check>
                  <Input
                    type="radio"
                    name="cancelWhen"
                    id="cancelWhenNow"
                    checked={cancelWhen === "now"}
                    disabled={cancelling}
                    onChange={() => setCancelWhen("now")}
                  />
                  <Label check for="cancelWhenNow">
                    {t("settings.subscription.cancel.whenNow")}
                  </Label>
                </FormGroup>
              </FormGroup>
            ) : null}
            <Alert
              color={cancelWhen === "now" ? "warning" : "info"}
              className="mb-0"
            >
              {cancelWhen === "now"
                ? t("settings.subscription.cancel.warningNow")
                : t("settings.subscription.cancel.warningPeriodEnd", {
                    end: periodEnd || "—",
                  })}
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={cancelling}
              onClick={() => setCancelOpen(false)}
            >
              {t("settings.subscription.checkout.cancel")}
            </Button>
            <Button color="danger" disabled={cancelling} onClick={runCancel}>
              {cancelling ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {t("settings.subscription.checkout.processing")}
                </>
              ) : cancelWhen === "period_end" ? (
                t("settings.subscription.cancel.confirmPeriodEndCta")
              ) : (
                t("settings.subscription.cancel.confirmCta")
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default SubscriptionSettingsPage;
